import React, { memo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import { Splash } from 'pages';
import { UserRole } from 'context';
import { authState } from 'core/state';
import { useLmsAccess } from 'core/hooks/Users/useLmsAccess';

import { PATHS } from './config';
import { isSuspensionAllowedPath } from './accessSuspension';

interface ILmsAccessGuard {
  children: React.ReactElement;
}

/**
 * ТЗ 0074: гард приостановки доступа — один на всё приложение.
 *
 * Приостановленный студент (активная роль Student) видит только /invoices и
 * страницы ошибок; любой другой адрес заменяется на /invoices. Преподаватель
 * и методист гард не замечают: блок в `me` бывает только у студенческой роли.
 *
 * Пока `me` студента не пришёл, закрытые страницы НЕ рендерятся (заставка):
 * иначе занятие успело бы мигнуть и разослать запросы, которые сервер всё
 * равно отобьёт.
 *
 * Пока доступ приостановлен, `me` опрашивается раз в минуту — оплату зачтёт
 * робот, и следующий ответ без блока отпустит студента без перезагрузки.
 */
export const LmsAccessGuard = memo(({ children }: ILmsAccessGuard) => {
  const { pathname } = useLocation();
  const { isLoggedIn, active_role } = useReactiveVar(authState);
  const { isSuspended, isResolved } = useLmsAccess({
    skip: !isLoggedIn,
    pollWhileSuspended: true,
  });

  if (!isLoggedIn || isSuspensionAllowedPath(pathname)) {
    return children;
  }
  if (isSuspended) {
    return <Navigate to={PATHS.invoices} replace />;
  }
  if (!isResolved && active_role === UserRole.STUDENT) {
    return <Splash />;
  }
  return children;
});
